import {
  DestroyRef,
  Injectable,
  InjectionToken,
  inject,
  signal,
} from '@angular/core';
import { BaseUser, UserType, userTypeFromJSON } from '@enrollhere/classes';
import {
  getIdToken,
  getIdTokenResult,
  onAuthStateChanged,
  type Unsubscribe,
  type User as FirebaseUser,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { AUTH, FIRESTORE } from '../firebase/firebase.tokens';
import { EnrollhereApiService } from '../enrollhere-api/enrollhere-api';

export const COMMON_AUTH_FIREBASE_FUNCTIONS = new InjectionToken<{
  getIdToken: typeof getIdToken;
  getIdTokenResult: typeof getIdTokenResult;
  onAuthStateChanged: typeof onAuthStateChanged;
  doc: typeof doc;
  onSnapshot: typeof onSnapshot;
}>('common.auth.firebase.functions', {
  factory: () => ({
    getIdToken,
    getIdTokenResult,
    onAuthStateChanged,
    doc,
    onSnapshot,
  }),
});

export interface AgencyMembership {
  agencyId: string;
  agencyName?: string;
  permission: {
    active: boolean;
    permissions: Record<string, unknown>;
    user?: Record<string, unknown>;
    permanentTags?: { list: string[] };
    invitedAt?: string | number;
    hasAccessFromParent?: string;
  };
}

export interface GetMeResponse {
  uid: string;
  userType: string;
  user: Record<string, unknown>;
  agencies: AgencyMembership[];
}

export interface RefreshMeOptions {
  force?: boolean;
}

/**
 * Identity bootstrap for common-side code. Talks to the Firebase JS SDK
 * directly via the `AUTH` + `FIRESTORE` injection tokens — no coupling to
 * `@angular/fire` or any host `AuthService`.
 *
 * Hosts MUST provide `AUTH` and `FIRESTORE` (see
 * `enrollhere-common/firebase/firebase.tokens.ts`). During the AngularFire
 * migration the providers are typically `useExisting` aliases of the
 * AngularFire-managed singletons.
 *
 * Lifecycle:
 * - Subscribes to `onAuthStateChanged` and refreshes `/v1/user/me` on sign-in.
 * - Watches `users/{uid}.lastClaimChange` and forces an ID-token refresh +
 *   `/v1/user/me` re-fetch when the field rotates (custom-claim updates).
 */
@Injectable({ providedIn: 'root' })
export class CommonAuthService {
  private readonly auth = inject(AUTH);
  private readonly firestore = inject(FIRESTORE);
  private readonly api = inject(EnrollhereApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly firebase = inject(COMMON_AUTH_FIREBASE_FUNCTIONS);

  private readonly _currentUser = signal<BaseUser | null>(null);
  private readonly _memberships = signal<AgencyMembership[]>([]);
  private readonly _userType = signal<UserType | null>(null);
  private readonly _ready = signal(false);
  private readonly _loadError = signal<unknown>(null);

  readonly currentUser = this._currentUser.asReadonly();
  readonly memberships = this._memberships.asReadonly();
  readonly userType = this._userType.asReadonly();
  readonly ready = this._ready.asReadonly();
  readonly loadError = this._loadError.asReadonly();

  private inflight: Promise<void> | undefined;
  private inflightUid: string | null | undefined;
  private inflightGeneration = 0;
  private refreshGeneration = 0;
  private sessionUid: string | null = null;

  /**
   * The signed-in uid as soon as Firebase restores its session, without
   * waiting on `/v1/user/me`. Caches keyed per user need this on a cold load —
   * `currentUser()` is still null while that call is in flight, which is
   * exactly when a cache would otherwise be skipped.
   */
  readonly signedInUid = (): string | null =>
    this.auth.currentUser?.uid ?? this.sessionUid ?? null;
  private claimsVersion: number | undefined;
  private claimsUnsub: Unsubscribe | undefined;
  private readonly authUnsub: Unsubscribe;
  private destroyed = false;

  constructor() {
    this.authUnsub = this.firebase.onAuthStateChanged(this.auth, fbUser =>
      this.onAuthChanged(fbUser)
    );
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      this.authUnsub();
      this.invalidateSession();
    });
  }

  getIdToken(forceRefresh = false): Promise<string> {
    const user = this.auth.currentUser;
    if (!user) {
      return Promise.reject(new Error('Not signed in'));
    }
    return this.firebase.getIdToken(user, forceRefresh);
  }

  /**
   * Fetch `/v1/user/me` and update the exposed signals. Concurrent callers
   * share a single in-flight request within the active session and refresh
   * generation. A forced refresh supersedes earlier work even under the same
   * UID. Resolves after signals are populated (or the failure is recorded on
   * `loadError`).
   */
  async refreshMe(options?: RefreshMeOptions): Promise<void> {
    if (this.destroyed) {
      return;
    }
    const uid = this.auth.currentUser?.uid ?? this.sessionUid ?? null;
    if (options?.force) {
      this.refreshGeneration += 1;
    } else if (
      this.inflight &&
      this.inflightGeneration === this.refreshGeneration &&
      this.inflightUid === uid
    ) {
      return this.awaitCurrentRefresh(uid);
    }

    const generation = this.refreshGeneration;
    const started = this.runRefresh(uid, generation);
    this.inflight = started;
    this.inflightUid = uid;
    this.inflightGeneration = generation;

    return this.awaitCurrentRefresh(uid);
  }

  /**
   * Wait for the current generation's refresh, then follow any same-UID
   * supersession until that later work settles. A replaced session returns
   * without adopting the new session's result.
   */
  private async awaitCurrentRefresh(uid: string | null): Promise<void> {
    while (true) {
      const generation = this.refreshGeneration;
      const current = this.inflight;
      if (current) {
        await current;
      }

      const currentUid = this.auth.currentUser?.uid ?? this.sessionUid ?? null;
      if (uid !== currentUid) {
        return;
      }
      if (generation !== this.refreshGeneration) {
        continue;
      }
      if (
        this.inflight &&
        this.inflight !== current &&
        this.inflightUid === uid
      ) {
        continue;
      }
      return;
    }
  }

  private async runRefresh(
    expectedUid: string | null,
    generation: number
  ): Promise<void> {
    try {
      const me = await this.api.get<GetMeResponse>('user/me');
      if (!this.isCurrentRefresh(expectedUid, generation)) {
        return;
      }
      if (expectedUid && me.uid && me.uid !== expectedUid) {
        return;
      }
      const user = me.user ? BaseUser.fromJSON(me.user) : null;
      this._currentUser.set(user);
      this._memberships.set(me.agencies ?? []);
      this._userType.set(me.userType ? userTypeFromJSON(me.userType) : null);
      this._loadError.set(null);
      this._ready.set(true);
    } catch (err: unknown) {
      if (!this.isCurrentRefresh(expectedUid, generation)) {
        return;
      }
      this._loadError.set(err);
    } finally {
      if (
        this.inflightGeneration === generation &&
        this.inflightUid === expectedUid
      ) {
        this.inflight = undefined;
        this.inflightUid = undefined;
      }
    }
  }

  private isCurrentRefresh(
    expectedUid: string | null,
    generation: number
  ): boolean {
    if (this.destroyed || generation !== this.refreshGeneration) {
      return false;
    }
    const currentUid = this.auth.currentUser?.uid ?? this.sessionUid ?? null;
    if (expectedUid !== currentUid) {
      return false;
    }
    return true;
  }

  private async onAuthChanged(fbUser: FirebaseUser | null): Promise<void> {
    if (this.destroyed) {
      return;
    }
    this.claimsUnsub?.();
    this.claimsUnsub = undefined;
    this.claimsVersion = undefined;

    if (!fbUser) {
      this.invalidateSession();
      return;
    }

    if (this.sessionUid && this.sessionUid !== fbUser.uid) {
      this.invalidateSession();
    }
    this.sessionUid = fbUser.uid;
    await this.refreshMe();
    if (this.sessionUid !== fbUser.uid) {
      return;
    }
    this.subscribeClaimsRefresh(fbUser);
  }

  /**
   * When the server bumps `users/{uid}.lastClaimChange` (custom-claim
   * rotation, role grant, etc.) force a token refresh so server calls see
   * the new claims, then re-fetch /v1/user/me so memberships/permissions
   * stay in sync.
   */
  /**
   * @deprecated Direct Firestore listener. Move claim-refresh signaling behind
   * the authenticated API boundary.
   */
  private subscribeClaimsRefresh(fbUser: FirebaseUser): void {
    const ownerUid = fbUser.uid;
    this.claimsUnsub = this.firebase.onSnapshot(
      this.firebase.doc(this.firestore, 'users', fbUser.uid),
      async snap => {
        if (this.destroyed || this.sessionUid !== ownerUid) {
          return;
        }
        const v = snap.data()?.['lastClaimChange'] as number | undefined;
        if (!v || this.claimsVersion === v) {
          return;
        }
        try {
          const tokenResult = await this.firebase.getIdTokenResult(fbUser);
          if (this.destroyed || this.sessionUid !== ownerUid) {
            return;
          }
          const issuedAtMs = Date.parse(tokenResult.issuedAtTime ?? '');
          this.claimsVersion = v;
          if (!(v > issuedAtMs)) {
            return;
          }
          await this.firebase.getIdTokenResult(fbUser, /*forceRefresh*/ true);
          if (this.destroyed || this.sessionUid !== ownerUid) {
            return;
          }
          await this.refreshMe({ force: true });
        } catch (err) {
          if (this.destroyed || this.sessionUid !== ownerUid) {
            return;
          }
          this._loadError.set(err);
        }
      }
    );
  }

  private invalidateSession(): void {
    this.refreshGeneration += 1;
    this.inflight = undefined;
    this.inflightUid = undefined;
    this.sessionUid = null;
    this.claimsUnsub?.();
    this.claimsUnsub = undefined;
    this.claimsVersion = undefined;
    this.reset();
  }

  private reset(): void {
    this._currentUser.set(null);
    this._memberships.set([]);
    this._userType.set(null);
    this._ready.set(false);
    this._loadError.set(null);
  }
}
